/**
 * «Переделать» — `POST …/request-changes {prompt, targets}`.
 *
 * Допустимые значения `targets` — `text` и `visual`, порядок бэкенд нормализует сам
 * (`services/backend/src/domain/decisions.ts`, `TARGET_ORDER`). `prompt` — 1..2000 символов,
 * и эта граница проверяется здесь тоже: не чтобы продублировать сервер, а чтобы человек не
 * узнал о ней после того, как написал текст.
 *
 * Чекбокс «визуал» включается по НАЛИЧИЮ ВЕРДИКТА, а не по наличию байтов картинки. Условие
 * `media_ref !== null` было верным ровно до появления вердикта и стало неверным в трёх
 * случаях сразу: сервис решил не рисовать (и это ровно тот сценарий, ради которого Илья
 * просил кнопку «нарисовать»), предложение висит непринятым, предложение отклонено. Во всех
 * трёх переделка визуала законна, а картинки в версии нет.
 *
 * МНЕНИЕ АВТОРА ПЕРЕДЕЛКА НЕ ТРОГАЕТ (AGI Boardroom, build spec 2026-11-02 §4). Если в тексте
 * версии стоит действующее мнение автора канала, писатель вынимает его до вызова модели и
 * вклеивает обратно дословно — диалог говорит это строкой: замечание «перепиши второй абзац»
 * над абзацем мнения иначе ждало бы правки, которой не будет. Менять само мнение — полоса «Мнение
 * автора» под постом, а не это замечание.
 */

import { postLabel } from '@smm/contracts/schemas';
import { type JSX, useState } from 'react';
import { ApiRequestError, post } from '../../api/client.ts';
import type { RenderModel, RequestChangesResponse } from '../../api/types.ts';
import { isAgentsOff } from '../../brand.tsx';
import { Modal } from '../../components/Modal.tsx';

const PROMPT_MAX = 2000;

export interface RequestChangesDialogProps {
  readonly model: RenderModel;
  readonly onChanged: () => void;
  readonly onClose: () => void;
}

export function RequestChangesDialog({
  model,
  onChanged,
  onClose,
}: RequestChangesDialogProps): JSX.Element {
  const [prompt, setPrompt] = useState('');
  const [text, setText] = useState(true);
  const [visual, setVisual] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canRedrawVisual = model.visual !== null;
  // Действующее мнение, которое стоит в тексте этой версии дословно, — его переделка переносит
  // (шапка файла). Вставка, ещё не дошедшая до текста, сюда не попадает: в «на переделке»
  // «Переделать…» нет вовсе.
  const keepsOpinion =
    model.author_opinion?.state === 'active' && model.author_opinion.in_version === true;
  const targets = [...(text ? ['text'] : []), ...(visual ? ['visual'] : [])];
  const trimmed = prompt.trim();
  const ready = trimmed.length > 0 && trimmed.length <= PROMPT_MAX && targets.length > 0;

  const submit = (): void => {
    if (!ready || busy) return;
    setBusy(true);
    setError(null);
    post<RequestChangesResponse>(
      `/api/v1/drafts/${model.draft_id}/versions/${model.version_no}/request-changes`,
      { prompt: trimmed, targets },
    )
      .then(() => {
        onChanged();
        onClose();
      })
      .catch((e: unknown) => {
        // Отказ бренда без сервиса генерации — тоже 409, но черновик не менялся: текст
        // сервера называет причину (docs/multi-brand.md §8).
        if (e instanceof ApiRequestError && e.status === 409 && !isAgentsOff(e)) {
          setError('Состояние черновика изменилось — обновите экран.');
          onChanged();
        } else {
          setError(e instanceof Error ? e.message : String(e));
        }
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <Modal
      title="Переделать"
      subtitle={`${postLabel(model.post_no)} · v${model.version_no}`}
      onClose={onClose}
    >
      <div className="stack" style={{ gap: 8 }}>
        <label className="lbl" htmlFor="rework-prompt" style={{ margin: 0 }}>
          Что переделать — замечание уйдёт сервису генерации промптом
        </label>
        <textarea
          id="rework-prompt"
          className="inp"
          rows={5}
          maxLength={PROMPT_MAX}
          value={prompt}
          placeholder="Например: убери канцелярит во втором абзаце, добавь цифру, ссылку оставь."
          onChange={(e) => {
            setPrompt(e.target.value);
          }}
        />
        <div className="lmeter">
          <span className="cnt num">
            {[...trimmed].length} / {PROMPT_MAX}
          </span>
          <span className="unit">символов</span>
        </div>
        {keepsOpinion ? (
          <div className="val val-info" data-testid="rework-keeps-opinion">
            <span>
              В посте мнение автора: переделка сохранит его дословно и перепишет только текст
              вокруг.
            </span>
          </div>
        ) : null}
      </div>

      <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
        <legend className="lbl" style={{ marginBottom: 6 }}>
          Что именно переделать
        </legend>
        <div style={{ display: 'flex', gap: 16 }}>
          <label className="small" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              type="checkbox"
              checked={text}
              onChange={(e) => {
                setText(e.target.checked);
              }}
            />
            текст
          </label>
          <label
            className="small"
            style={{
              display: 'flex',
              gap: 8,
              alignItems: 'center',
              opacity: canRedrawVisual ? 1 : 0.5,
            }}
          >
            <input
              type="checkbox"
              checked={visual}
              disabled={!canRedrawVisual}
              onChange={(e) => {
                setVisual(e.target.checked);
              }}
            />
            визуал
          </label>
        </div>
        {canRedrawVisual ? (
          model.visual?.verdict === 'none' && model.media_ref === null ? (
            <div className="micro" style={{ marginTop: 6 }}>
              Сервис решил не рисовать. Отметьте «визуал» и опишите, что должно быть на картинке.
            </div>
          ) : null
        ) : (
          <div className="micro" style={{ marginTop: 6 }}>
            Вердикта по визуалу у этой версии нет — переделывать нечего.
          </div>
        )}
      </fieldset>

      {/* Ход переделки виден с правки 11 (§9.2): у переделки появился свой журнал прогресса
          (`rework_id` в 0017) и человеческий маршрут очереди, а полоса в шапке опрашивает оба
          маршрута. Прежняя фраза «полосы хода у переделки нет» стала ложью и снята вместе с
          правкой — оставленное объяснение снятого поведения хуже отсутствия объяснения. */}
      <div className="notice">
        Черновик уйдёт в статус «на переделке», замечание ляжет в журнал, и сервис генерации возьмёт
        его ближайшим опросом — секунды, а не «следующим проходом». Ход виден в шапке, слева от
        кнопки запуска. Единственный выход из «на переделки» — приход версии v+1: если сервис
        откажет (например, рисовать по описанию нечего), причина появится там же в полосе и придёт
        алертом на «Мониторинг», а версия останется прежней.
      </div>

      {error === null ? null : (
        <div className="val val-err">
          <span>{error}</span>
        </div>
      )}

      <div className="stack" style={{ gap: 10 }}>
        <button
          type="button"
          className={ready && !busy ? 'btn btn-pri btn-w' : 'btn btn-w btn-dis'}
          disabled={!ready || busy}
          onClick={submit}
        >
          Отправить на переделку
        </button>
        <button type="button" className="btn btn-ghost btn-w" onClick={onClose}>
          Отмена
        </button>
      </div>
    </Modal>
  );
}
